/* State machine hook for YouTube subscription import.
   Steps: connect → fetching → selecting → importing → results */

import { useState, useCallback, useMemo } from "react";
import { useQuery, useAction, useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import { extractYouTubeChannelId } from "@/lib/youtubeHelpers";
import { useFeatureTracking } from "@/hooks/useFeatureTracking";

export type YouTubeImportStep =
  | "connect"
  | "fetching"
  | "selecting"
  | "importing"
  | "results";

interface YouTubeChannel {
  id: string;
  title: string;
  thumbnail?: string | null;
}

interface ImportResults {
  imported: number;
  failed: number;
}

export function useYouTubeImport() {
  const [step, setStep] = useState<YouTubeImportStep>("connect");
  const [channels, setChannels] = useState<YouTubeChannel[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [importResults, setImportResults] = useState<ImportResults | null>(null);
  const [error, setError] = useState<string | null>(null);

  const connections = useQuery(api.youtubeSources.listConnections);
  const sources = useQuery(api.sources.list);
  const getAuthUrl = useAction(api.youtubeSources.getYouTubeAuthUrl);
  const listSubscriptions = useAction(api.youtubeSources.listYouTubeSubscriptions);
  const createSource = useMutation(api.sources.create);
  const { trackFeature } = useFeatureTracking();

  const existingChannelIds = useMemo(() => {
    if (!sources) return new Set<string>();
    const ids = new Set<string>();
    for (const source of sources) {
      if (source.type !== "youtube") continue;
      if (source.youtubeChannelId) {
        ids.add(source.youtubeChannelId);
      }
      const extracted = extractYouTubeChannelId(source.url);
      if (extracted) ids.add(extracted);
    }
    return ids;
  }, [sources]);

  const hasConnection = connections && connections.length > 0;

  const startOAuth = useCallback(async () => {
    try {
      setError(null);
      const redirectUri = `${window.location.origin}/sources`;
      const url = await getAuthUrl({ redirectUri });
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to start OAuth");
    }
  }, [getAuthUrl]);

  const fetchChannels = useCallback(async () => {
    setStep("fetching");
    setError(null);
    try {
      const result = await listSubscriptions();
      setChannels(result.channels);
      const selectable = result.channels
        .filter((ch) => !existingChannelIds.has(ch.id))
        .map((ch) => ch.id);
      setSelectedIds(new Set(selectable));
      setStep("selecting");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch subscriptions");
      setStep("connect");
    }
  }, [listSubscriptions, existingChannelIds]);

  const toggleChannel = useCallback((channelId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(channelId)) {
        next.delete(channelId);
      } else {
        next.add(channelId);
      }
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    const selectable = channels
      .filter((ch) => !existingChannelIds.has(ch.id))
      .map((ch) => ch.id);
    setSelectedIds(new Set(selectable));
  }, [channels, existingChannelIds]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const confirmImport = useCallback(async () => {
    if (selectedIds.size === 0) return;
    setStep("importing");
    setError(null);

    const toImport = channels.filter((ch) => selectedIds.has(ch.id));
    const results = await Promise.allSettled(
      toImport.map((ch) =>
        createSource({
          name: ch.title,
          type: "youtube",
          url: `https://www.youtube.com/channel/${ch.id}`,
        }),
      ),
    );

    const imported = results.filter((r) => r.status === "fulfilled").length;
    const failed = results.filter((r) => r.status === "rejected").length;

    if (imported > 0) trackFeature("youtube_source_added");
    setImportResults({ imported, failed });
    setStep("results");
  }, [selectedIds, channels, createSource, trackFeature]);

  const reset = useCallback(() => {
    setStep("connect");
    setChannels([]);
    setSelectedIds(new Set());
    setImportResults(null);
    setError(null);
  }, []);

  return {
    step,
    channels,
    selectedIds,
    existingChannelIds,
    importResults,
    error,
    hasConnection,
    startOAuth,
    fetchChannels,
    toggleChannel,
    selectAll,
    clearSelection,
    confirmImport,
    reset,
  };
}
